import { useState, useEffect, useRef } from 'react';

// A column's filter, for a filter row under a table's headers (PMO Dashboard,
// Milestones Reporting). A column definition's `filter` is either
// { text: placeholder }, a box matched against what is typed, or
// { all: label, value: row => v, options?: rows => [[v, label]] }, a tick-box
// list of the values in the rows. passesColumnFilters in utils/columnLayout.js
// applies them.

// A card fixed to the window beside the control that opened it, so the table's
// sideways scroll cannot clip it. Escape, a click elsewhere or a scroll outside
// the card closes it; scrolling a long list inside it does not.
export function useAnchoredCard() {
  const [anchor, setAnchor] = useState(null); // rect of the opening control while the card is open
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!anchor) return;
    const onKey = e => { if (e.key === 'Escape') setAnchor(null); };
    const onOutside = e => { if (!wrapRef.current?.contains(e.target)) setAnchor(null); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onOutside);
    window.addEventListener('scroll', onOutside, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onOutside);
      window.removeEventListener('scroll', onOutside, true);
    };
  }, [anchor]);

  // The S / M / L text size sets CSS zoom on .content, which scales a fixed
  // element's offsets, so the control's on-screen rect is taken into that space.
  const open = el => {
    const z = el.currentCSSZoom || 1;
    const r = el.getBoundingClientRect();
    setAnchor({ left: r.left / z, top: r.top / z, bottom: r.bottom / z, vw: window.innerWidth / z, vh: window.innerHeight / z });
  };

  return { anchor, setAnchor, wrapRef, open };
}

export default function ColumnFilter({ filter, rows, value, onChange }) {
  const { anchor, setAnchor, wrapRef, open } = useAnchoredCard();

  if (filter.text) {
    return <input className="w-full text-[8px] border border-gray-200 rounded px-1.5 py-0.5 bg-white placeholder-gray-300 focus:outline-none focus:border-navy" placeholder={filter.text} value={value || ''} onChange={e => onChange(e.target.value)} />;
  }

  const options = filter.options ? filter.options(rows) : [...new Set(rows.map(filter.value).filter(Boolean))].sort().map(v => [v, v]);
  const picked = value || [];
  const labelOf = v => options.find(([o]) => o === v)?.[1] ?? v;
  const toggle = v => onChange(picked.includes(v) ? picked.filter(p => p !== v) : [...picked, v]);
  const summary = !picked.length ? filter.all : `${labelOf(picked[0])}${picked.length > 1 ? ` +${picked.length - 1}` : ''}`;

  return (
    <div ref={wrapRef}>
      <button
        type="button"
        aria-expanded={!!anchor}
        onClick={e => (anchor ? setAnchor(null) : open(e.currentTarget))}
        title={picked.map(labelOf).join(', ') || undefined}
        className={`w-full flex items-center justify-between gap-1 text-[8px] border rounded px-1 py-0.5 bg-white text-left whitespace-nowrap focus:outline-none focus:border-navy ${picked.length ? 'border-navy text-navy font-semibold' : 'border-gray-200 text-gray-600 font-normal'}`}
      >
        <span className="truncate">{summary}</span>
        <span className="text-gray-400" aria-hidden="true">▾</span>
      </button>
      {anchor && (
        <div
          role="dialog"
          aria-label={filter.all}
          className="fixed z-50 min-w-[160px] max-w-[260px] bg-white border border-gray-200 rounded shadow-lg p-1.5 normal-case tracking-normal"
          style={{ left: Math.min(anchor.left, anchor.vw - 268), top: anchor.bottom + 2 }}
        >
          <ul className="max-h-60 overflow-y-auto">
            {options.map(([v, l]) => (
              <li key={v}>
                <label className="flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" className="w-3 h-3 accent-gold" checked={picked.includes(v)} onChange={() => toggle(v)} />
                  <span className="text-[9px] font-normal text-gray-700 truncate">{l}</span>
                </label>
              </li>
            ))}
          </ul>
          {picked.length > 0 && (
            <div className="mt-1 pt-1 border-t border-gray-100 px-1">
              <button type="button" onClick={() => onChange([])} className="text-[8px] text-blue-500 hover:underline font-semibold">Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
